// Define the type for custom selections
import { TreatmentKeys } from '@chiroup/core/constants/treatmentKeys';
import { ordinalEn } from '../functions/chainTitle';
import { CustomSelection } from '../types/CustomSelection.type';

export enum PlanWizardCategoryEnum {
  EvaluationManagement = 'Evaluation & Management',
  Treatments = 'Treatments',
  Modalities = 'Modalities',
  SupplementsSupplies = 'Supplements & Supplies',
  Rehab = 'Rehab',
  ImagingTests = 'Imaging & Tests',
}
export const PLAN_WIZARD_CATEGORIES_ARR = [
  PlanWizardCategoryEnum.EvaluationManagement,
  PlanWizardCategoryEnum.ImagingTests,
  PlanWizardCategoryEnum.Treatments,
  PlanWizardCategoryEnum.Modalities,
  PlanWizardCategoryEnum.SupplementsSupplies,
  PlanWizardCategoryEnum.Rehab,
];

/**
 * Shown everywhere the re-eval countdown/interval is set. The mechanic (see
 * previewSurveys.ts `isReEval` and getCarePlan.ts `nextVisitType`): each encounter
 * stores the prior encounter's countdown minus one, and the visit AFTER the one whose
 * stored value is 0 fires the re-eval — that re-eval visit's own countdown then resets
 * from the provider's interval setting. So a value of N means N more visits happen,
 * then the (N+1)th visit is the re-exam; 0 means the next visit is it. Takes the
 * field's current value so the wording tracks what the user typed (blank/invalid
 * falls back to the example of 5).
 */
export const reEvalIntervalExplainer = (interval?: number | string | null): string => {
  // Number('') is 0, so an emptied field must be treated as "no value", not 0.
  const raw = typeof interval === 'string' ? interval.trim() : interval;
  const n = raw === '' || raw === null || raw === undefined ? Number.NaN : Math.floor(Number(raw));
  const valid = Number.isFinite(n) && n >= 0;
  const shown = valid ? n : 5;

  if (valid && shown === 0) {
    return 'A value of 0 means the next visit will be the re-exam.';
  }

  return `A value of ${shown} means the re-exam will happen on the ${ordinalEn(shown + 1)} visit — the visit after ${shown} more ${
    shown === 1 ? 'visit is' : 'visits are'
  } completed.`;
};

export const SOAP_PREFERENCE_SETTING_NAMES = {
  SOAP_PREFERENCES: 'Soap Preferences',
  SURVEYS_FOR_SOAP_NOTES: 'Surveys for soap notes',
  OBJECTIVE_ROM: 'Objective ROM',
  SOAP_NOTE_DETAILS: 'SOAP Note Details',
  PLAN_WIZARD_CATEGORIES: 'Plan Wizard Categories',
  RE_EXAM_ALERT: 'Re-Exam Alert',
} as const;

// "Re-Exam Alert" toggle default: ON. Scoped to the provider who owns the
// encounter — suppresses the alert DISPLAY only; the countdown itself always runs.
export const reExamAlertDefault = true;

export const categoriesSelections: Record<PlanWizardCategoryEnum, CustomSelection[]> = {
  [PlanWizardCategoryEnum.Modalities]: [],
  [PlanWizardCategoryEnum.SupplementsSupplies]: [],
  [PlanWizardCategoryEnum.EvaluationManagement]: [
    {
      id: -1,
      baseId: 1,
      name: 'New Patient 15-29 min',
      primaryCptCode: '99202',
      isSystemDefault: true,
      includeCptInOutput: false,
      hideInPlan: false,
      output: 'New Patient 15-29 min (insert units)',
      maxUnits: 1,
      order: 1,
    },
    {
      id: -2,
      baseId: 2,
      name: 'New Patient 30-44 min',
      primaryCptCode: '99203',
      isSystemDefault: true,
      includeCptInOutput: false,
      hideInPlan: false,
      output: 'New Patient 30-44 min (insert units)',
      maxUnits: 1,
      order: 2,
    },
    {
      id: -3,
      baseId: 3,
      name: 'New Patient 45-59 min',
      primaryCptCode: '99204',
      isSystemDefault: true,
      includeCptInOutput: false,
      hideInPlan: false,
      output: 'New Patient 45-59 min (insert units)',
      maxUnits: 1,
      order: 3,
    },
    {
      id: -4,
      baseId: 4,
      name: 'Est. Patient 10-19 min',
      primaryCptCode: '99212',
      isSystemDefault: true,
      includeCptInOutput: false,
      hideInPlan: false,
      output: 'Est. Patient 10-19 min (insert units)',
      maxUnits: 1,
      order: 4,
    },
    {
      id: -5,
      baseId: 5,
      name: 'Est. Patient 20-29 min',
      primaryCptCode: '99213',
      includeCptInOutput: false,
      isSystemDefault: true,
      hideInPlan: false,
      output: 'Est. Patient 20-29 min (insert units)',
      maxUnits: 1,
      order: 5,
    },
    {
      id: -6,
      baseId: 6,
      name: 'Est. Patient 30-39 min',
      primaryCptCode: '99214',
      includeCptInOutput: false,
      isSystemDefault: true,
      hideInPlan: false,
      output: 'Est. Patient 30-39 min (insert units)',
      maxUnits: 1,
      order: 6,
    },
    {
      id: -7,
      baseId: 7,
      name: 'Est. Patient ~5 min',
      primaryCptCode: '99211',
      includeCptInOutput: false,
      isSystemDefault: true,
      hideInPlan: false,
      output: 'Est. Patient ~5 min (insert units)',
      maxUnits: 1,
      order: 7,
    },
  ],
  [PlanWizardCategoryEnum.Treatments]: [
    {
      id: TreatmentKeys.CMT_SPINAL_1_TO_2_REGIONS,
      baseId: 1001,
      name: 'CMT, Spinal 1-2 Regions',
      subCategory: 'cmt',
      maxUnits: 1,
      primaryCptCode: '98940',
      includeCptInOutput: false,
      isSystemDefault: true,
      hideInPlan: false,
      output: 'CMT, Spinal 1-2 Regions (insert units)',
      order: 1,
    },
    {
      id: TreatmentKeys.CMT_SPINAL_3_TO_4_REGIONS,
      baseId: 1002,
      name: 'CMT, Spinal 3-4 Regions',
      subCategory: 'cmt',
      maxUnits: 1,
      primaryCptCode: '98941',
      includeCptInOutput: false,
      isSystemDefault: true,
      hideInPlan: false,
      output: 'CMT, Spinal 3-4 Regions (insert units)',
      order: 2,
    },
    {
      id: TreatmentKeys.CMT_SPINAL_5_REGIONS,
      baseId: 1003,
      name: 'CMT, Spinal 5 Regions',
      subCategory: 'cmt',
      maxUnits: 1,
      primaryCptCode: '98942',
      includeCptInOutput: false,
      isSystemDefault: true,
      hideInPlan: false,
      output: 'CMT, Spinal 5 Regions (insert units)',
      order: 3,
    },
    {
      id: TreatmentKeys.CMT_EXTRASPINAL_1_PLUS,
      baseId: 1004,
      name: 'CMT, Extraspinal 1+',
      subCategory: 'cmtExtraspinal',
      maxUnits: 1,
      primaryCptCode: '98943',
      includeCptInOutput: false,
      isSystemDefault: true,
      hideInPlan: false,
      output: 'CMT, Extraspinal 1+ (insert units)',
      order: 4,
    },
    {
      id: TreatmentKeys.MASSAGE_THERAPY,
      baseId: 1005,
      name: 'Massage Therapy',
      subCategory: 'massageTherapy',
      maxUnits: 4,
      primaryCptCode: '97124',
      isSystemDefault: true,
      includeCptInOutput: false,
      hideInPlan: false,
      output:
        'Massage Therapy (insert units/minutes): The indicated units and corresponding minutes of massage therapy were performed on the earlier documented hypertonic muscles in the spine and distinctly separate limb girdle areas. Specific techniques included effleurage, petrissage and tapotement.',
      order: 5,
    },
    {
      id: TreatmentKeys.MANUAL_THERAPY,
      baseId: 1006,
      name: 'Manual Therapy',
      subCategory: 'manualTherapy',
      maxUnits: 4,
      primaryCptCode: '97140',
      isSystemDefault: true,
      includeCptInOutput: false,
      hideInPlan: false,
      output:
        'Manual Therapy (insert units/minutes): The indicated units and corresponding minutes of hands-on myofascial release were performed on the earlier documented hypomobile joints, connective tissues, and muscles in the spine and distinctly separate limb girdle areas. The goal of this manual therapy is to relieve discomfort and restore proper joint and muscle function. Specific techniques included joint mobilization, soft tissue mobilization, stretching, and manual traction.',
      order: 6,
    },
    {
      id: TreatmentKeys.ACUPUNCTURE,
      baseId: 1007,
      name: 'Acupuncture',
      subCategory: 'acupuncture',
      maxUnits: 4,
      //   services: ['97810'],
      primaryCptCode: '97810',
      secondaryCptCode: '97811',
      //   unitServices: { 1: ['97810'], __: ['97811'] },
      isSystemDefault: true,
      includeCptInOutput: false,
      output: 'Acupuncture (insert units/minutes)',
      hideInPlan: false,
      order: 7,
    },
    {
      id: TreatmentKeys.ACUPUNCTURE_WITH_E_STIM,
      baseId: 1008,
      name: 'Acupuncture with E-Stim',
      subCategory: 'acupunctureWithEStim',
      maxUnits: 4,
      primaryCptCode: '97813',
      secondaryCptCode: '97814',
      hideInPlan: false,
      isSystemDefault: true,
      includeCptInOutput: false,
      output: 'Acupuncture with E-Stim (insert units/minutes)',
      order: 8,
    },
    {
      id: TreatmentKeys.DRY_NEEDLING_1_TO_2_MUSCLES,
      baseId: 1009,
      name: 'Dry Needling, 1-2 Muscles',
      subCategory: 'dryNeedling',
      maxUnits: 1,
      primaryCptCode: '20560',
      hideInPlan: false,
      isSystemDefault: true,
      includeCptInOutput: false,
      output:
        'Dry Needling, 1-2 Muscles (insert units): Dry needle insertion without injection was performed in 1-2 muscles with the goal of increasing blood flow, relieving pain, and improving movement.',
      order: 9,
    },
    {
      id: TreatmentKeys.DRY_NEEDLING_3_PLUS_MUSCLES,
      baseId: 1010,
      name: 'Dry Needling, 3+ Muscles',
      subCategory: 'dryNeedling',
      maxUnits: 1,
      primaryCptCode: '20561',
      hideInPlan: false,
      isSystemDefault: true,
      includeCptInOutput: false,
      output:
        'Dry Needling, 3+ Muscles (insert units): Dry needle insertion without injection was performed in 3 or more muscles with the goal of increasing blood flow, relieving pain, and improving movement.',
      order: 10,
    },
  ],
  [PlanWizardCategoryEnum.ImagingTests]: [],
  [PlanWizardCategoryEnum.Rehab]: [
    {
      id: -1,
      baseId: 2001,
      name: 'Therapeutic Exercise',
      subCategory: 'therapeuticExercise',
      maxUnits: 4,
      primaryCptCode: '97110',
      hideInPlan: false,
      isSystemDefault: true,
      includeCptInOutput: false,
      output:
        "Therapeutic Exercise (insert units/minutes): Therapeutic exercises for improving range of motion, flexibility, endurance, and strength were prescribed. I explained each exercise in detail, and the exercises were demonstrated for the indicated units and corresponding minutes. Details of specific exercises are outlined in the patient's subsequent exercise log.",
      order: 1,
    },
    {
      id: -2,
      baseId: 2002,
      name: 'Neuromuscular Reeducation',
      subCategory: 'neuromuscularReeducation',
      maxUnits: 4,
      primaryCptCode: '97112',
      isSystemDefault: true,
      includeCptInOutput: false,
      hideInPlan: false,
      output:
        'Neuromuscular Reeducation (insert units/minutes): Therapeutic procedure, 1 or more areas, each 15 minutes; neuromuscular reeducation of movement, balance, coordination, kinesthetic sense, posture, and/or proprioception for sitting and/or standing activities.',
      order: 2,
    },
    {
      id: -3,
      baseId: 2003,
      name: 'Therapeutic Activity',
      subCategory: 'therapeuticActivity',
      maxUnits: 4,
      primaryCptCode: '97530',
      isSystemDefault: true,
      includeCptInOutput: false,
      hideInPlan: false,
      output:
        "Therapeutic Activity (insert units/minutes): Dynamic therapeutic activities were performed for the indicated units and corresponding minutes. These included activities to enhance functional performance across various parameters, including balance, coordination, power, strength, and range of motion. The patient's subsequent exercise log outlines details of the specific activities.",
      order: 3,
    },
  ],
};

const defaultBaseIds = {
  cmt: 1001,
  cmtExtraspinal: 1002,
  massageTherapy: 1003,
  manualTherapy: 1004,
  acupuncture: 1005,
  acupunctureWithEStim: 1006,
  dryNeedling: 1007,
  therapeuticExercise: 2001,
  neuromuscularReeducation: 2002,
  therapeuticActivity: 2003,
};

const allTheBASES = [
  ...categoriesSelections[PlanWizardCategoryEnum.Treatments],
  ...categoriesSelections[PlanWizardCategoryEnum.Rehab],
  ...categoriesSelections[PlanWizardCategoryEnum.EvaluationManagement],
];

export const baseIdsToID = allTheBASES.reduce(
  (obj, item: any) => {
    obj[item.baseId] = typeof item.id === 'number' ? Math.abs(item.id) : item.id;

    return obj;
  },
  {} as Record<number, number>
);

export const categoriesSelectionsDefaultHash = Object.keys(categoriesSelections || {})?.reduce(
  (obj, category) => {
    obj[category] = obj[category] || {};
    categoriesSelections[category as PlanWizardCategoryEnum].forEach((item: CustomSelection) => {
      // let id;

      obj[category][item.baseId] = item;
    });

    return obj;
  },
  {} as Record<string, Record<string | number, CustomSelection>>
);
